import { provideNzI18n, vi_VN, en_US } from 'ng-zorro-antd/i18n';
import { registerLocaleData } from '@angular/common';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { HttpBackend, HttpClient, provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import {
  ApplicationConfig,
  EnvironmentProviders,
  inject,
  isDevMode,
  provideAppInitializer,
} from '@angular/core';
import {
  provideRouter,
  RouteReuseStrategy,
  withEnabledBlockingInitialNavigation,
  withInMemoryScrolling,
} from '@angular/router';
import { CustomRouteReuseStrategy } from './core/strategies/custom-route-reuse.strategy';
import { NzConfig, provideNzConfig } from 'ng-zorro-antd/core/config';
import { provideEchartsCore } from 'ngx-echarts';
import * as echarts from 'echarts';

import { routes } from './app.routes';
import { SimpleReuseStrategy } from '@core/services/common/reuse-strategy';
import * as AllIcons from '@ant-design/icons-angular/icons';
import { provideEffects } from '@ngrx/effects';
import { provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { NzIconService, provideNzIcons } from 'ng-zorro-antd/icon';
import { I18nService } from '@app/core/services/common/i18n.service';
import { customIconEntries } from '@app/shared/components/icon-sel/custom-icons';
import { DynamicRouteService } from '@app/pages/page-builder/services/dynamic-route.service';
import { ApiInterceptor } from './core/services/interceptors/api.interceptor';
import { rootReducers } from './store';
import { rootEffects } from './store/effects';
import { SubWindowWithService } from './core/services/common/sub-window-with.service';
import { ThemeSkinService } from './core/services/common/theme-skin.service';
import { InitThemeService } from './core/services/common/init-theme.service';
import { KioskConfigService } from './pages/kios/services/kiosk-config.service';
import {
  AppInitializerService,
  appInitializerFactory,
} from './core/services/common/app-initializer.service';
import { EnvService } from './core/services/common/env.service';

export function initEnv(envService: EnvService) {
  return async () => await envService.load();
}

// Initialize i18n
function initializeI18n(i18nService: I18nService) {
  return () => i18nService.initLanguage();
}

export function InitKioskConfigFactory(kioskConfigService: KioskConfigService) {
  return () => kioskConfigService.loadConfig();
}
// Initialize dynamic routes
function initializeDynamicRoutes(dynamicRouteService: DynamicRouteService) {
  return () => dynamicRouteService.initializeDynamicRoutes();
}

export function SubWindowWithServiceFactory(subWindowWithService: SubWindowWithService) {
  return () => subWindowWithService.subWindowWidth();
}

export function InitThemeServiceFactory(initThemeService: InitThemeService) {
  return async (): Promise<void> => await initThemeService.initTheme();
}

const icons = Object.keys(AllIcons).map((key) => (AllIcons as any)[key]);

/**
 * Get saved language code
 */
const getSavedLanguage = (): string => {
  const stored = localStorage.getItem('app_language') as string;
  return I18nService.isLanguageSupported(stored) ? stored : 'VI';
};

const currentLang = getSavedLanguage();

// Load and register Angular locale data dynamically
(async () => {
  const angularLocale = await I18nService.getAngularLocale(currentLang);
  registerLocaleData(angularLocale);
})();

// Get ng-zorro locale based on language
const getNzLocale = () => {
  switch (currentLang) {
    case 'EN':
      return en_US;
    case 'VI':
    default:
      return vi_VN;
  }
};

const ngZorroConfig: NzConfig = {
  theme: {
    primaryColor: '#1890ff',
  },
};

const APPINIT_PROVIDES: EnvironmentProviders[] = [
  provideAppInitializer(() => {
    const initializerFn = InitThemeServiceFactory(inject(InitThemeService));
    return initializerFn();
  }),
  provideAppInitializer(() => {
    const initializerFn = SubWindowWithServiceFactory(inject(SubWindowWithService));
    return initializerFn();
  }),
  provideAppInitializer(() => {
    const initializerFn = ((themeService: ThemeSkinService) => () => {
      return themeService.loadTheme();
    })(inject(ThemeSkinService));
    return initializerFn();
  }),
];

export const appConfig: ApplicationConfig = {
  providers: [
    // 1. QUAN TRỌNG: Load env.json đầu tiên

    provideAppInitializer(async () => {
      const env = initEnv(inject(EnvService));
      const kiosk = InitKioskConfigFactory(inject(KioskConfigService));
      await env();
      return await kiosk();
    }),

    // 2. Setup HTTP và Router - cần có trước khi gọi API
    provideHttpClient(withFetch(), withInterceptors([ApiInterceptor])),
    provideRouter(
      routes,
      withInMemoryScrolling({ anchorScrolling: 'enabled', scrollPositionRestoration: 'enabled' }),
      withEnabledBlockingInitialNavigation(),
    ),
    // Route Reuse Strategy - để giữ state khi chuyển tab
    { provide: RouteReuseStrategy, useClass: CustomRouteReuseStrategy },
    // Route Reuse Strategy - cache components khi chuyển tab
    { provide: RouteReuseStrategy, useClass: SimpleReuseStrategy },

    // 3. Setup UI libraries
    provideNzI18n(getNzLocale()),
    provideAnimationsAsync(),
    provideNzIcons(icons),
    provideNzConfig(ngZorroConfig),
    provideEchartsCore({ echarts }),

    // 4. Theme và UI initializers (không phụ thuộc env config)
    ...APPINIT_PROVIDES,

    // 4.1. Register custom SVG icons (src/assets/icons/)
    provideAppInitializer(() => {
      const iconService = inject(NzIconService);
      const http = new HttpClient(inject(HttpBackend));
      customIconEntries.filter(e => e.isSvg).forEach(entry => {
        http.get(`assets/icons/${entry.file}`, { responseType: 'text' }).subscribe(svg => {
          iconService.addIconLiteral(`custom:${entry.name}`, svg);
        });
      });
    }),

    // 5. NgRx Store Configuration
    provideStore(rootReducers),
    provideEffects(rootEffects),
    provideStoreDevtools({ maxAge: 25, logOnly: !isDevMode() }),

    // 6. I18n initialization
    provideAppInitializer(() => {
      const initializerFn = initializeI18n(inject(I18nService));
      return initializerFn();
    }),

    // 7. Dynamic routes initialization
    provideAppInitializer(() => {
      const initializerFn = initializeDynamicRoutes(inject(DynamicRouteService));
      return initializerFn();
    }),

    // 9. App initializer cuối cùng
    provideAppInitializer(() => {
      const initializerFn = appInitializerFactory(inject(AppInitializerService));
      return initializerFn();
    }),
  ],
};
